import { Component } from '@theme/component'
import {
  ThemeEvents,
  VariantSelectedEvent,
  VariantSelectedOptionsChangedEvent,
  VariantUpdateEvent,
} from '@theme/events'
import { morph } from '@theme/morph'
import { ProductFormComponent } from '@theme/product-form'

/**
 * A custom element that manages a variant picker.
 *
 * @typedef {Object} VariantPickerRefs
 * @property {HTMLElement[]} variantOptions - The variant options.
 *
 * @extends {Component<VariantPickerRefs>}
 */
export default class VariantPicker extends Component {
  requiredRefs = ['variantOptions']

  /** @type {string | undefined} */
  #pendingRequestUrl

  /** @type {AbortController | undefined} */
  #abortController

  /** @type {NodeListOf<ProductFormComponent> | null} */
  #productFormComponents = null

  /** @type {{id: number, options: string[], available: boolean}[]} */
  #productVariants = []

  /** @type {boolean} */
  #isInitialized = false

  /** @type {number} */
  #fetchRequestToken = 0

  connectedCallback() {
    super.connectedCallback()

    // Prevent duplicate initialization
    if (this.#isInitialized) {
      return
    }
    this.#isInitialized = true

    const timeout = setTimeout(() => {
      this.#productFormComponents = this.dataset.productFormSelector
        ? document.querySelectorAll(this.dataset.productFormSelector)
        : null
      try {
        this.#productVariants = JSON.parse(
          this.querySelector('script.product-variants')?.textContent ?? '[]'
        )
      } catch {
        this.#productVariants = []
      }

      this.#refreshAvailability()
      clearTimeout(timeout)
    }, 0)

    // Add event listeners only once
    if (!this.hasAttribute('data-listeners-added')) {
      this.addEventListener('change', this.variantChanged)
      window.addEventListener(
        ThemeEvents.variantSelectedOptionsChanged,
        /** @type {EventListener} */ (this.handleSelectedOptionsChanged)
      )
      this.setAttribute('data-listeners-added', 'true')
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    // Remove event listeners
    this.removeEventListener('change', this.variantChanged)
    window.removeEventListener(
      ThemeEvents.variantSelectedOptionsChanged,
      /** @type {EventListener} */ (this.handleSelectedOptionsChanged)
    )

    // Reset initialization state
    this.#isInitialized = false
    this.removeAttribute('data-listeners-added')
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged = (event) => {
    if (!(event.target instanceof HTMLElement)) return

    const selectedElement = event.target

    this.updateSelectedOption(event.target)

    // Recompute cross-outs on every change, including partial selections - the
    // gate below only fetches a section once every option is picked.
    this.#refreshAvailability()
    this.#dispatchSelectedOptionsChanged()

    const { optionValueId } = event.target.dataset
    this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId ?? '' }))

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog')

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0]
    const newUrl = event.target.dataset.connectedProductUrl
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl

    let variantId = ''
    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === 'radio'
    ) {
      variantId = event.target.dataset.variantId || ''
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex]
      variantId = selectedOption?.dataset.variantId || ''
    }

    // Compute whether all options have a concrete selection (based on current state, not attributes).
    /** @type {NodeListOf<HTMLInputElement | HTMLOptionElement>} */
    const selectedOptions = this.querySelectorAll(
      'fieldset input:checked, select option[selected]'
    )
    const allOptionsSelected = this.#areAllOptionsSelected(selectedOptions)

    this.#fetchRequestToken += 1
    const requestToken = this.#fetchRequestToken

    if (allOptionsSelected) {
      this.fetchUpdatedSection(
        this.buildRequestUrl(selectedElement),
        loadsNewProduct,
        optionValueId,
        variantId
      ).then(() => {
        if (requestToken !== this.#fetchRequestToken) {
          return
        }

        const url = new URL(window.location.href)

        /** @type {NodeListOf<HTMLInputElement | HTMLOptionElement>} */
        const nextSelectedOptions = this.querySelectorAll(
          'fieldset input:checked, select option[selected]'
        )

        if (isOnProductPage) {
          if (variantId) {
            const urlCanUseVariant =
              this.#areAllOptionsSelected(nextSelectedOptions)

            if (urlCanUseVariant) {
              url.searchParams.set('variant', variantId)
            }
          } else {
            url.searchParams.delete('variant')
          }
        }

        this.#refreshAvailability()
        this.#dispatchSelectedOptionsChanged()

        // Change the path if the option is connected to another product via combined listing.
        if (loadsNewProduct && newUrl) {
          url.pathname = newUrl
        }

        if (url.href !== window.location.href) {
          history.replaceState({}, '', url.toString())
        }
      })
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`
      )

      if (!targetElement) throw new Error('Target element not found')

      target = targetElement
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue
      )

      if (!newSelectedOption) throw new Error('Option not found')

      for (const option of target.options) {
        option.removeAttribute('selected')
      }

      newSelectedOption.setAttribute('selected', 'selected')
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl
    this.#pendingRequestUrl = productUrl
    const params = []

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`)
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`)
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`)
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest('quick-add-component') ||
      this.closest('swatches-variant-picker-component')
    ) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0]
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join(
        '&'
      )}`
    }
    return `${productUrl}?${params.join('&')}`
  }

  /**
   * Disables or enables buttons.
   * @param {boolean} disabled - If the buttons should be disabled.
   */
  #setButtonsDisabled(disabled) {
    const buttons = this.querySelectorAll('[data-connected-product-url]')
    buttons.forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.disabled = disabled
      }
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false')
    })
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {string | undefined} optionValueId - The option value ID.
   * @param {string | undefined} variantId - The variant ID.
   */
  fetchUpdatedSection(
    requestUrl,
    shouldMorphMain = false,
    optionValueId = '',
    variantId = ''
  ) {
    // Cancel previous request if it exists
    this.#abortController?.abort()
    this.#abortController = new AbortController()

    return fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer')

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`
        )?.textContent

        if (!textContent) {
          this.#setButtonsDisabled(false)
          return
        }

        // Parse once; use the server-authoritative variant ID everywhere to
        // avoid stale data-variant-id values captured at click time.
        const serverVariant = JSON.parse(textContent)
        const resolvedVariantId = serverVariant?.id
          ? String(serverVariant.id)
          : variantId

        /** @type {NewProduct | undefined} */
        let newProduct
        if (shouldMorphMain) {
          this.updateMain(html)
        } else {
          newProduct = this.updateVariantPicker(html)
        }

        // Update selected options FIRST so selectedOptionId works correctly
        const selectedOptions = this.updateSelectedOptions(optionValueId)

        // Now dispatch the event after DOM is updated
        if (!shouldMorphMain && this.selectedOptionId) {
          const variantUpdateEvent = new VariantUpdateEvent(
            serverVariant,
            this.selectedOptionId,
            {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            }
          )
          this.dispatchEvent(variantUpdateEvent)
        }

        const productFormComponentContent =
          this.getProductFormComponentContent(html)

        if (productFormComponentContent) {
          const { addToCartText, canAddToCart } = productFormComponentContent
          this.updateProductFormComponents(
            selectedOptions,
            addToCartText,
            canAddToCart,
            resolvedVariantId
          )

          const allOptionsSelected =
            this.#areAllOptionsSelected(selectedOptions)

          // Dispatch event for inventory visibility
          document.dispatchEvent(
            new CustomEvent('variantSelectionUpdate', {
              detail: {
                allOptionsSelected,
                productId: this.dataset.productId,
              },
            })
          )
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user')
        } else {
          console.error(error)
        }
      })
      .finally(() => {
        // unlock buttons
        this.#setButtonsDisabled(false)
      })
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase()
    )

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found')
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId
      const newProductUrl = newVariantPickerSource.dataset.productUrl

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl }
      }

      this.dataset.productId = newProductId
      this.dataset.productUrl = newProductUrl
    }

    morph(this, newVariantPickerSource)

    return newProduct
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main')
    const newMain = newHtml.querySelector('main')

    if (!main || !newMain) {
      throw new Error('No new main source found')
    }

    morph(main, newMain)
  }

  /**
   * Updates the selected options.
   * @param {string | undefined} optionValueId - The option value ID.
   */
  updateSelectedOptions(optionValueId) {
    let selector = `input[checked], option[selected]`
    if (optionValueId) {
      selector = `[data-option-value-id="${optionValueId}"], ${selector}`
    }

    /** @type {NodeListOf<HTMLInputElement | HTMLOptionElement>} */
    const selectedOptions = this.querySelectorAll(selector)
    if (selectedOptions.length) {
      selectedOptions.forEach((option) => {
        if (option instanceof HTMLInputElement) {
          option.checked = true
        } else if (option instanceof HTMLOptionElement) {
          option.selected = true
        }
      })
    }

    return selectedOptions
  }

  /**
   * Gets the product form component content.
   * @param {Document} sectionHtml - The section HTML.
   * @returns {ProductFormComponentContent | null} The product form component content.
   * @typedef {Object} ProductFormComponentContent
   * @property {string} addToCartText - The add to cart text.
   * @property {boolean} canAddToCart - If the product can be added to the cart.
   */
  getProductFormComponentContent(sectionHtml) {
    const productFormComponent = sectionHtml.querySelector(
      'product-form-component'
    )
    if (!productFormComponent) {
      return null
    }

    return {
      addToCartText:
        productFormComponent.getAttribute('data-add-to-cart-text') ?? '',
      canAddToCart:
        productFormComponent.getAttribute('data-can-add-to-cart') === 'true',
    }
  }

  /**
   * Updates the product form component.
   * @param {NodeListOf<HTMLInputElement | HTMLOptionElement>} selectedOptions - The selected options.
   * @param {string} addToCartText - The add to cart text.
   * @param {boolean} canAddToCart - If the product can be added to the cart.
   */
  updateProductFormComponents(
    selectedOptions,
    addToCartText,
    canAddToCart,
    variantId = ''
  ) {
    if (!this.#productFormComponents?.length) {
      return
    }

    this.#productFormComponents.forEach((productFormComponent) => {
      const { addToCartButtonContainer } = productFormComponent.refs
      if (!addToCartButtonContainer) {
        return
      }

      const allOptionsSelected = this.#areAllOptionsSelected(selectedOptions)
      const allAddToCartButtons = [
        ...Array.from(
          document.querySelectorAll('.sticky-atc__add-to-cart-button')
        ).filter((button) => button instanceof HTMLButtonElement),
        ...(addToCartButtonContainer?.refs.addToCartButton
          ? [addToCartButtonContainer.refs.addToCartButton]
          : []),
      ]
      allAddToCartButtons.forEach((button) => {
        if (!allOptionsSelected) {
          button.disabled = true
          button.textContent = Theme.translations.selectASize || 'SELECT A SIZE'
          return
        }
        button.disabled = !canAddToCart
        button.textContent = addToCartText
      })

      // Show/hide pickup availability based on canAddToCart and selection completeness
      try {
        const localPickup = productFormComponent.querySelector('local-pickup')
        if (localPickup) {
          if (canAddToCart && allOptionsSelected) {
            localPickup.removeAttribute('hidden')
          } else {
            localPickup.setAttribute('hidden', '')
          }
        }
      } catch (error) {
        // Log error to aid debugging; consider removing in production if desired
        console.error('Error updating local pickup availability:', error)
      }
    })
  }

  /**
   * Handles the variant picker selected options changed event.
   * @param {VariantSelectedOptionsChangedEvent} event - The variant picker selected options changed event.
   */
  handleSelectedOptionsChanged = (event) => {
    const { optionsIds, source } = event.detail
    if (source === this || !optionsIds.length || !this.#productFormComponents) {
      return
    }

    const sourceOptions = source.querySelectorAll('input, option')
    const thisElementOptions = this.querySelectorAll('input, option')
    sourceOptions.forEach((sourceOption, index) => {
      const elementOption = thisElementOptions[index]
      if (!elementOption) {
        return
      }

      elementOption.setAttribute(
        'data-option-value-id',
        sourceOption.getAttribute('data-option-value-id') ?? ''
      )
      elementOption.setAttribute(
        'data-variant-id',
        sourceOption.getAttribute('data-variant-id') ?? ''
      )
      elementOption.setAttribute(
        'data-connected-product-url',
        sourceOption.getAttribute('data-connected-product-url') ?? ''
      )

      if (
        elementOption instanceof HTMLOptionElement &&
        sourceOption instanceof HTMLOptionElement
      ) {
        elementOption.value = sourceOption.value
        elementOption.selected = sourceOption.selected
      } else if (
        elementOption instanceof HTMLInputElement &&
        sourceOption instanceof HTMLInputElement
      ) {
        elementOption.setAttribute(
          'aria-disabled',
          sourceOption.getAttribute('aria-disabled') ?? ''
        )
        elementOption.setAttribute(
          'data-unavailable',
          sourceOption.getAttribute('data-unavailable') ?? ''
        )
        elementOption.setAttribute(
          'aria-label',
          sourceOption.getAttribute('aria-label') ?? ''
        )
        elementOption.value = sourceOption.value
        elementOption.checked = sourceOption.checked
      }
    })
  }

  /**
   * Marks every option value that is sold out or that does not exist as a
   * variant, given whatever is currently selected. Runs on partial selections
   * too: unset option groups simply don't constrain the match.
   */
  #refreshAvailability() {
    if (!this.#productVariants.length) return

    /** @type {HTMLFieldSetElement[]} */
    const groups = Array.from(this.querySelectorAll('fieldset[data-option]'))
    if (!groups.length) return

    /** @param {HTMLElement} group */
    const positionOf = (group) =>
      Number(group.dataset.option?.replace('option', '')) - 1

    /** @type {(string|null)[]} - current selection, indexed by option position */
    const selection = []
    for (const group of groups) {
      const index = positionOf(group)
      if (!Number.isInteger(index) || index < 0) continue

      /** @type {HTMLInputElement | null} */
      const checked = group.querySelector('input:checked')
      selection[index] = checked?.value ?? null
    }

    for (const group of groups) {
      const index = positionOf(group)
      if (!Number.isInteger(index) || index < 0) continue

      for (const input of group.querySelectorAll('input')) {
        if (!(input instanceof HTMLInputElement)) continue

        // Combined-listing values belong to a sibling product, so this
        // product's variants say nothing about them - leave them alone.
        if (input.dataset.connectedProductUrl) {
          this.updateOptionState({
            option: input,
            ariaDisabled: false,
            dataUnavailable: false,
          })
          continue
        }

        // The selection we would have if this value were the one picked.
        const candidate = selection.slice()
        candidate[index] = input.value

        const matches = this.#productVariants.filter((variant) =>
          candidate.every(
            (value, position) =>
              value == null || variant.options?.[position] === value
          )
        )

        // Written on every pass so stale cross-outs clear.
        this.updateOptionState({
          option: input,
          ariaDisabled: !matches.some((variant) => variant.available),
          dataUnavailable: matches.length === 0,
        })
      }
    }
  }

  /**
   * Mirrors the current selection to other pickers for the same product
   * (e.g. the sticky add-to-cart bar), partial selections included.
   */
  #dispatchSelectedOptionsChanged() {
    /** @type {NodeListOf<HTMLInputElement | HTMLOptionElement>} */
    const selectedOptions = this.querySelectorAll(
      'fieldset input:checked, select option[selected]'
    )
    const optionsIds = Array.from(selectedOptions)
      .map((option) => option.dataset.optionValueId)
      .filter(Boolean)

    window.dispatchEvent(
      new VariantSelectedOptionsChangedEvent(optionsIds, this)
    )
  }

  /**
   * Updates the state of an option.
   * @param {object} params - The parameters.
   * @param {HTMLInputElement} params.option - The option to update.
   * @param {boolean} params.ariaDisabled - The aria-disabled state.
   * @param {boolean} [params.dataUnavailable] - (Optional) The data-unavailable state.
   */
  updateOptionState({ option, ariaDisabled, dataUnavailable }) {
    option.setAttribute('aria-disabled', ariaDisabled.toString())

    if (this.dataset.disableOutOfStockVariantsSelection === 'true') {
      option.disabled = ariaDisabled
    }

    if (dataUnavailable !== undefined) {
      option.setAttribute('data-unavailable', dataUnavailable.toString())
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked'
    )

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined
    }

    return selectedOption
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this
    if (!selectedOption) return undefined
    const { optionValueId } = selectedOption.dataset

    if (!optionValueId) {
      throw new Error('No option value ID found')
    }

    return optionValueId
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    )

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset

      if (!optionValueId) throw new Error('No option value ID found')

      return optionValueId
    })
  }

  /**
   * Determines if all variant option groups have a concrete selection.
   * @param {NodeListOf<HTMLInputElement | HTMLOptionElement>} selectedOptions
   * @returns {boolean}
   */
  #areAllOptionsSelected(selectedOptions) {
    const totalOptions = this.refs.variantOptions?.length ?? 0
    if (!totalOptions) return false
    return selectedOptions.length === totalOptions
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker)
}
